/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as Entities from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { lAC_ITSGetRetailStoreAddressAsync } from '../custom-actions/DataActionExtension.g';

const STORE_ADDRESS_STORAGE_KEY = 'lac-store-address-';

/** The address this tab already read for the channel, so a reload does not ask the store again. */
const readKeptStoreAddress = (channelId: number): Entities.OrgUnitAddress | undefined => {
    if (!Msdyn365.msdyn365Commerce.isBrowser) {
        return undefined;
    }

    try {
        const kept = window.sessionStorage.getItem(STORE_ADDRESS_STORAGE_KEY + channelId);

        return kept ? (JSON.parse(kept) as Entities.OrgUnitAddress) : undefined;
    } catch (error) {
        console.log('LTS Info', 'get-retail-store-address: the kept address could not be read', error);

        return undefined;
    }
};

const keepStoreAddress = (channelId: number, address: Entities.OrgUnitAddress): void => {
    if (!Msdyn365.msdyn365Commerce.isBrowser) {
        return;
    }

    try {
        window.sessionStorage.setItem(STORE_ADDRESS_STORAGE_KEY + channelId, JSON.stringify(address));
    } catch (error) {
        console.log('LTS Info', 'get-retail-store-address: the address could not be kept for the next page', error);
    }
};

/**
 * GetRetailStoreAddress Input Action
 */
export class GetRetailStoreAddressInput implements Msdyn365.IActionInput {
    public channelId: number;

    constructor(channelId: number) {
        this.channelId = channelId;
    }

    public getCacheKey = () => `GETRETAILSTOREADDRESS_${this.channelId}`;
    public getCacheObjectType = () => 'GETRETAILSTOREADDRESS';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

async function getRetailStoreAddressAsync(
    input: GetRetailStoreAddressInput,
    ctx: Msdyn365.IActionContext
): Promise<Entities.OrgUnitAddress | undefined> {
    const kept = readKeptStoreAddress(input.channelId);
    if (kept) {
        return kept;
    }

    try {
        const result = await lAC_ITSGetRetailStoreAddressAsync({ callerContext: ctx }, input.channelId);
        if (result) {
            keepStoreAddress(input.channelId, result);
            return result;
        }
    } catch (error) {
        console.log('Error getting retail store address:', error);
        throw error;
    }
    return undefined;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<Entities.OrgUnitAddress | undefined>>getRetailStoreAddressAsync,
    id: 'ITSGetRetailStoreAddress'
});
